import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import SignaturePad from "../../components/SignaturePad";
import { ArrowLeft, Camera, Image as ImageIcon, Trash2, Check, AlertCircle, RotateCcw } from "lucide-react";

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function AdminDelivery() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [signedBy, setSignedBy] = useState("");
  const [comment, setComment] = useState("");
  const sigRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => {
      setOrder(r.data);
      setSignedBy(`${r.data.recipient?.first_name || ""} ${r.data.recipient?.last_name || ""}`.trim());
    }).finally(() => setLoading(false));
  }, [id]);

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const data = await Promise.all(files.map(fileToDataURL));
    setPhotos((p) => [...p, ...data]);
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));
  const clearSig = () => sigRef.current?.clear();

  const onSubmit = async () => {
    setError(null);
    if (sigRef.current?.isEmpty()) {
      setError("La signature est obligatoire");
      return;
    }
    if (!signedBy.trim()) {
      setError("Veuillez saisir le nom du signataire");
      return;
    }
    setSubmitting(true);
    try {
      const signature = sigRef.current.toDataURL();
      await api.post(`/admin/orders/${id}/delivery`, {
        photos,
        signature,
        signed_by: signedBy,
        comment,
      });
      nav(`/admin/orders/${id}`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>;
  if (!order) return <div className="lc-card p-6">Commande introuvable.</div>;

  return (
    <div data-testid="admin-delivery-page">
      <Link to={`/admin/orders/${id}`} className="text-xs text-[var(--lc-gray)] hover:text-[var(--lc-black)] inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Retour à la commande
      </Link>
      <div className="mb-5">
        <div className="lc-small-label mb-1">Mode livraison</div>
        <h1 className="display text-2xl sm:text-3xl font-extrabold tracking-tight">Bon de livraison</h1>
        <div className="text-xs text-[var(--lc-gray)] mt-1 font-mono">{order.ref}</div>
      </div>

      {/* Récap commande */}
      <div className="lc-card p-4 sm:p-5 mb-4">
        <div className="text-sm font-semibold">{order.recipient?.first_name} {order.recipient?.last_name}</div>
        <div className="text-sm text-[var(--lc-gray)]">{order.recipient?.address}</div>
        <div className="text-xs text-[var(--lc-gray)] mt-2">
          {order.total_packages} colis · {order.total_weight_kg} kg · {order.oversized_count} hors gabarit
        </div>
      </div>

      {error && (
        <div data-testid="delivery-error" className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* Photos */}
      <div className="lc-card p-4 sm:p-5 mb-4">
        <div className="lc-small-label mb-3">Photos de livraison</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative group">
              <img src={p} alt={`photo-${i}`} className="w-full h-24 object-cover rounded-md border border-[var(--lc-border)]" />
              <button
                onClick={() => removePhoto(i)}
                data-testid={`delivery-remove-photo-${i}`}
                className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="h-14 border-2 border-dashed border-[var(--lc-border)] rounded-md flex items-center justify-center gap-2 cursor-pointer text-[var(--lc-gray)] hover:border-[var(--lc-orange)] hover:text-[var(--lc-orange)] font-semibold text-sm" data-testid="delivery-take-photo">
            <Camera className="w-4 h-4" />
            <span>Prendre une photo</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPickFile}
              className="hidden"
              data-testid="delivery-camera-input"
            />
          </label>
          <label className="h-14 border-2 border-dashed border-[var(--lc-border)] rounded-md flex items-center justify-center gap-2 cursor-pointer text-[var(--lc-gray)] hover:border-[var(--lc-orange)] hover:text-[var(--lc-orange)] font-semibold text-sm" data-testid="delivery-pick-gallery">
            <ImageIcon className="w-4 h-4" />
            <span>Choisir depuis la galerie</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onPickFile}
              className="hidden"
              data-testid="delivery-gallery-input"
            />
          </label>
        </div>
      </div>

      {/* Comment */}
      <div className="lc-card p-4 sm:p-5 mb-4">
        <div className="lc-small-label mb-3">Commentaire de livraison <span className="text-[var(--lc-gray)] normal-case font-normal">(facultatif)</span></div>
        <textarea
          data-testid="delivery-comment"
          rows="3"
          className="lc-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Remarques éventuelles : état des colis, conditions d'accès rencontrées, réserves du destinataire..."
        />
      </div>

      {/* Signature */}
      <div className="lc-card p-4 sm:p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="lc-small-label">Signature du destinataire</div>
          <button onClick={clearSig} data-testid="delivery-clear-signature" className="text-xs text-[var(--lc-gray)] hover:text-[var(--lc-black)] inline-flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Effacer
          </button>
        </div>
        <SignaturePad ref={sigRef} />
        <div className="mt-3">
          <label className="lc-label">Nom du signataire</label>
          <input
            data-testid="delivery-signed-by"
            className="lc-input"
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
          />
        </div>
      </div>

      <button
        data-testid="delivery-submit"
        onClick={onSubmit}
        disabled={submitting}
        className="lc-btn-primary w-full inline-flex items-center justify-center gap-2"
      >
        <Check className="w-4 h-4" /> {submitting ? "Génération du BL…" : "Valider la livraison & générer le BL"}
      </button>
    </div>
  );
}
